'use client'

import { CirclePauseIcon, OctagonAlertIcon, WrenchIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  exchangeDisplayName,
  formatDuration,
  RegularHours,
} from '@/components/widget/market-status-badge'
import { cn } from '@/lib/utils'
import { isAlwaysOpen, useVenueSchedule, useVenueStatus } from '@/service/venue-status.hook'
import { Exchange } from '@/types/generated/common/types'
import { ExchangeStatusType } from '@/types/generated/public_api'

interface MarketStatusNoticeProps {
  exchange: Exchange | undefined
  className?: string
}

/** Compact, non-dismissible warning for a selected market that is not open. */
export function MarketStatusNotice({ exchange, className }: MarketStatusNoticeProps) {
  const t = useTranslations('venueStatusBanner')
  const { data: status, isError } = useVenueStatus(exchange)
  const schedule = useVenueSchedule(exchange)

  // Tick once a minute so the "opens in" countdown stays current.
  const [now, setNow] = useState(() => Date.now())
  const hasBoundary = status?.nextChangeMs !== undefined
  useEffect(() => {
    if (!hasBoundary) return
    const interval = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(interval)
  }, [hasBoundary])

  if (!exchange || !status || isError || isAlwaysOpen(status)) return null
  if (status.status === ExchangeStatusType.EXCHANGE_STATUS_TYPE_OPEN) return null

  const exchangeName = exchangeDisplayName(exchange)
  const isOutage = status.status === ExchangeStatusType.EXCHANGE_STATUS_TYPE_UNPLANNED_OUTAGE
  const isMaintenance = status.status === ExchangeStatusType.EXCHANGE_STATUS_TYPE_MAINTENANCE
  const title = isOutage
    ? t('outage', { exchange: exchangeName })
    : isMaintenance
      ? t('maintenance', { exchange: exchangeName })
      : t('closed', { exchange: exchangeName })
  const StatusIcon = isOutage ? OctagonAlertIcon : isMaintenance ? WrenchIcon : CirclePauseIcon

  const boundaryMs = status.nextChangeMs !== undefined ? Number(status.nextChangeMs) : undefined
  const countdown =
    boundaryMs !== undefined && boundaryMs > now
      ? `Opens in ${formatDuration(boundaryMs - now)}`
      : undefined

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="status"
          aria-label={title}
          className={cn(
            'inline-flex h-6 max-w-56 shrink-0 cursor-help items-center gap-1.5 border px-2 text-[11px] font-medium',
            isOutage
              ? 'border-chart-red/35 bg-chart-red/10 text-chart-red'
              : 'border-ox-gold/30 bg-ox-gold/10 text-ox-gold',
            className
          )}
        >
          <StatusIcon className="size-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{title}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium">{exchangeName}</span>
          <span className="text-xs text-white/60">{title}</span>
          {countdown && <span className="text-xs text-white/60">{countdown}</span>}
        </div>
        {schedule && <RegularHours schedule={schedule} />}
      </TooltipContent>
    </Tooltip>
  )
}
